import React, { useMemo, useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Form, Space, Input } from 'antd';
import ExcelTable from 'components/ui/excel-table';
import {
  ECellValueType,
  EExcelTablePaginationType,
  ExcelTableColDef,
} from 'components/ui/excel-table/types';
import usePagination from 'hooks/usePagination';
import { BinderContractData } from 'components/features/binder-contract-table/form';
import { useQuery } from 'react-query';
import { contractApi } from 'services/apis';
import { EQueryKeys } from 'constants/query-keys';
import dayjs from 'dayjs';
import { format } from 'date-fns';
import {
  ContractResponseDtoContractStatusEnum,
  FilterContractsFieldEnum,
  FilterContractsOrderEnum,
} from 'libs/pinnacle-sdk';
import ContractStatusIndicator from 'components/ui/contract-status-indicator';
import { EBinderContractTab } from 'components/features/binder-contract-table/dashboard-tabs/types';
import BinderContractTabs from 'components/features/binder-contract-table/dashboard-tabs';
import { RowClickedEvent, SortChangedEvent } from 'ag-grid-community';
import { useNavigate } from 'react-router';
import {
  constructContractDetailPath,
  constructSubmissionDetailPath,
  EPageFrom,
  EPageTo,
} from 'constants/path';
import FilterButton from 'components/ui/filter-button';
import { SortedSelect } from 'components/ui/sorted-select';
import { getLocalDateForMidnightUTC } from 'utils';
import { useDashboardFiltersStore } from 'stores/dashboard-filters';

const BinderContractTable: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { binderContractTable: savedFilters, setBinderContractTableFilters } =
    useDashboardFiltersStore();

  const [form] = Form.useForm();
  const [isFilterOpen, setIsFilterOpen] = useState(savedFilters.isFilterOpen);
  const [activeTab, setActiveTab] = useState(savedFilters.activeTab);
  const [sortField, setSortField] = useState<
    FilterContractsFieldEnum | undefined
  >(savedFilters.sortField);
  const [sortOrder, setSortOrder] = useState<
    FilterContractsOrderEnum | undefined
  >(savedFilters.sortOrder);
  const {
    currentPage,
    pageSize,
    onPageChange,
    onShowSizeChange,
    resetPagination,
  } = usePagination(500);

  // Restore saved filters to form
  useEffect(() => {
    form.setFieldsValue({
      pinnacleRefNo: savedFilters.searchPinnacleRefNo,
      legacyPinRefNo: savedFilters.searchLegacyPinRefNo,
      insureds: savedFilters.selectedInsureds,
      reinsureds: savedFilters.selectedReinsureds,
      brokers: savedFilters.selectedBrokers,
      binders: savedFilters.selectedBinders,
      lineOfBusiness: savedFilters.selectedLineOfBusiness,
      underwriters: savedFilters.selectedUnderwriters,
      underwritingYears: savedFilters.selectedUnderwritingYears,
      statuses: savedFilters.selectedStatuses,
    });
  }, []);

  // Watch form values for filtering
  const searchPinnacleRefNo = Form.useWatch('pinnacleRefNo', form);
  const searchLegacyPinRefNo = Form.useWatch('legacyPinRefNo', form);
  const selectedInsureds = Form.useWatch('insureds', form);
  const selectedReinsureds = Form.useWatch('reinsureds', form);
  const selectedBrokers = Form.useWatch('brokers', form);
  const selectedBinders = Form.useWatch('binders', form);
  const selectedLineOfBusiness = Form.useWatch('lineOfBusiness', form);
  const selectedUnderwriters = Form.useWatch('underwriters', form);
  const selectedUnderwritingYears = Form.useWatch('underwritingYears', form);
  const selectedStatuses = Form.useWatch('statuses', form);
  const currentYearStartDate = useMemo(
    () =>
      activeTab === EBinderContractTab.CURRENT_YEAR
        ? dayjs().startOf('year').toDate()
        : undefined,
    [activeTab]
  );
  const currentYearEndDate = useMemo(
    () =>
      activeTab === EBinderContractTab.CURRENT_YEAR
        ? dayjs().endOf('year').toDate()
        : undefined,
    [activeTab]
  );

  // Save filters to store whenever they change
  useEffect(() => {
    setBinderContractTableFilters({
      searchPinnacleRefNo,
      searchLegacyPinRefNo,
      selectedInsureds,
      selectedReinsureds,
      selectedBrokers,
      selectedBinders,
      selectedLineOfBusiness,
      selectedUnderwriters,
      selectedUnderwritingYears,
      selectedStatuses,
      sortField,
      sortOrder,
      isFilterOpen,
      activeTab,
    });
  }, [
    searchPinnacleRefNo,
    searchLegacyPinRefNo,
    selectedInsureds,
    selectedReinsureds,
    selectedBrokers,
    selectedBinders,
    selectedLineOfBusiness,
    selectedUnderwriters,
    selectedUnderwritingYears,
    selectedStatuses,
    sortField,
    sortOrder,
    isFilterOpen,
    activeTab,
    setBinderContractTableFilters,
  ]);

  const { data: options } = useQuery(
    [EQueryKeys.CONTRACT_FILTER_OPTIONS, activeTab],
    () => {
      return contractApi.getFilterOptions({
        contractFilterOptionsQueryDto: {
          isMyContract: activeTab === EBinderContractTab.MY_CONTRACT,
          startDate: currentYearStartDate,
          endDate: currentYearEndDate,
        },
      });
    },
    {
      refetchOnWindowFocus: false,
    }
  );

  // Reset pagination when filters change
  useEffect(() => {
    resetPagination();
  }, [
    activeTab,
    sortField,
    sortOrder,
    searchLegacyPinRefNo,
    searchPinnacleRefNo,
    selectedInsureds,
    selectedReinsureds,
    selectedBrokers,
    selectedBinders,
    selectedLineOfBusiness,
    selectedUnderwriters,
    selectedUnderwritingYears,
    selectedStatuses,
    resetPagination,
  ]);

  const { data: contracts, isLoading: isLoadingContracts } = useQuery(
    [
      EQueryKeys.CONTRACT_LIST,
      currentPage,
      pageSize,
      activeTab,
      sortField,
      sortOrder,
      searchPinnacleRefNo,
      searchLegacyPinRefNo,
      selectedInsureds,
      selectedReinsureds,
      selectedBrokers,
      selectedBinders,
      selectedLineOfBusiness,
      selectedUnderwriters,
      selectedUnderwritingYears,
      selectedStatuses,
    ],
    () =>
      contractApi.filterContracts({
        contractFilterQueryDto: {
          includeBinder: true,
          includeLayer: true,
          includeBroker: true,
          includeSubmission: true,
          includeReinsured: true,
          isMyContract: activeTab === EBinderContractTab.MY_CONTRACT,
          startDate: currentYearStartDate,
          endDate: currentYearEndDate,
          pinnacleRefNo: searchPinnacleRefNo,
          legacyPinRefNo: searchLegacyPinRefNo,
          insuredIds: selectedInsureds,
          reinsuredIds: selectedReinsureds,
          brokerIds: selectedBrokers,
          binderIds: selectedBinders,
          lineOfBusinesses: selectedLineOfBusiness,
          underwriterIds: selectedUnderwriters,
          underwritingYears: selectedUnderwritingYears,
          statuses: selectedStatuses,
        },
        page: currentPage,
        pageSize: pageSize,
        field: sortField,
        order: sortOrder,
      })
  );

  // Filter options
  const brokerOptions = useMemo(
    () =>
      options?.brokers.map((broker) => ({
        label: broker.name,
        value: broker.id,
      })),
    [options]
  );

  const insuredOptions = useMemo(
    () =>
      options?.insured.map((insured) => ({
        label: insured.name,
        value: insured.id,
      })),
    [options]
  );

  const reinsuredOptions = useMemo(
    () =>
      options?.reinsured.map((reinsured) => ({
        label: reinsured.name,
        value: reinsured.id,
      })),
    [options]
  );

  const binderOptions = useMemo(
    () =>
      options?.binders.map((binder) => ({
        label: binder.name,
        value: binder.id,
      })),
    [options]
  );

  const underwriterOptions = useMemo(
    () =>
      options?.underwriters.map((user) => ({
        label: user.name,
        value: user.id,
      })),
    [options]
  );

  const lineOfBusinessOptions = useMemo(
    () =>
      options?.lineOfBusinesses.map((line) => ({
        label: line,
        value: line,
      })),
    [options]
  );

  const underwritingYearOptions = useMemo(() => {
    return options?.underwritingYears.map((year) => ({
      label: year,
      value: year,
    }));
  }, [options]);

  const statusOptions = useMemo(
    () => [
      {
        label: (
          <ContractStatusIndicator
            status={ContractResponseDtoContractStatusEnum.Pending}
          />
        ),
        value: ContractResponseDtoContractStatusEnum.Pending,
      },
      {
        label: (
          <ContractStatusIndicator
            status={ContractResponseDtoContractStatusEnum.Quoted}
          />
        ),
        value: ContractResponseDtoContractStatusEnum.Quoted,
      },
      {
        label: (
          <ContractStatusIndicator
            status={ContractResponseDtoContractStatusEnum.Bound}
          />
        ),
        value: ContractResponseDtoContractStatusEnum.Bound,
      },
      {
        label: (
          <ContractStatusIndicator
            status={ContractResponseDtoContractStatusEnum.NotTakenUp}
          />
        ),
        value: ContractResponseDtoContractStatusEnum.NotTakenUp,
      },
      {
        label: (
          <ContractStatusIndicator
            status={ContractResponseDtoContractStatusEnum.Cancelled}
          />
        ),
        value: ContractResponseDtoContractStatusEnum.Cancelled,
      },
    ],
    []
  );

  const columnDefs = useMemo<ExcelTableColDef[]>(
    () => [
      {
        colId: FilterContractsFieldEnum.PinnacleRefNo,
        headerName: t('contractAdjustment.pinnacleReferenceNumber'),
        field: 'pinnacleRefNo',
        flex: 1,
        editable: false,
        minWidth: 150,
        resizable: true,
        unSortIcon: true,
        sortable: true,
        comparator: () => 0,
      },
      {
        colId: 'oldPinReferenceNumber',
        headerName: t('contractAdjustment.legacyPINReferenceNumber'),
        field: 'oldPinReferenceNumber',
        flex: 1,
        editable: false,
        minWidth: 150,
        resizable: true,
        unSortIcon: false,
        sortable: false,
        comparator: () => 0,
      },
      {
        colId: FilterContractsFieldEnum.Insured,
        headerName: t('insured.insured'),
        field: 'insured',
        flex: 1,
        editable: false,
        minWidth: 150,
        resizable: true,
        unSortIcon: true,
        sortable: true,
        comparator: () => 0,
      },
      {
        colId: FilterContractsFieldEnum.Reinsured,
        headerName: t('reinsured.reinsured'),
        field: 'reinsured',
        flex: 1,
        editable: false,
        minWidth: 150,
        resizable: true,
        unSortIcon: true,
        sortable: true,
        comparator: () => 0,
      },
      {
        colId: FilterContractsFieldEnum.SourceBroker,
        headerName: t('cashReceipts.sourceBroker'),
        field: 'sourceBroker',
        flex: 1,
        editable: false,
        minWidth: 150,
        resizable: true,
        unSortIcon: true,
        sortable: true,
        comparator: () => 0,
      },
      {
        colId: FilterContractsFieldEnum.Binder,
        headerName: t('binder.binder'),
        field: 'binder',
        flex: 1,
        editable: false,
        minWidth: 150,
        resizable: true,
        unSortIcon: true,
        sortable: true,
        comparator: () => 0,
      },
      {
        colId: FilterContractsFieldEnum.LineOfBusiness,
        headerName: t('submission.lineOfBusiness'),
        field: 'lineOfBusiness',
        flex: 1,
        editable: false,
        minWidth: 150,
        resizable: true,
        unSortIcon: true,
        sortable: true,
        comparator: () => 0,
      },
      {
        colId: FilterContractsFieldEnum.Underwriter,
        headerName: t('submission.assignedHandler'),
        field: 'assignedHandler',
        flex: 1,
        editable: false,
        minWidth: 150,
        resizable: true,
        unSortIcon: true,
        sortable: true,
        comparator: () => 0,
      },
      {
        colId: FilterContractsFieldEnum.NamedUnderwriter,
        headerName: t('submission.namedUnderwriter'),
        field: 'namedUnderwriter',
        flex: 1,
        editable: false,
        minWidth: 150,
        resizable: true,
        unSortIcon: true,
        sortable: true,
        comparator: () => 0,
      },
      {
        colId: FilterContractsFieldEnum.UnderwritingYear,
        headerName: t('submission.underwritingYear'),
        field: 'underwritingYear',
        flex: 1,
        editable: false,
        minWidth: 150,
        resizable: true,
        unSortIcon: true,
        sortable: true,
        comparator: () => 0,
      },
      {
        colId: FilterContractsFieldEnum.InceptionDate,
        headerName: t('submission.inceptionDate'),
        field: 'inceptionDate',
        flex: 1,
        editable: false,
        minWidth: 150,
        resizable: true,
        unSortIcon: true,
        sortable: true,
        comparator: () => 0,
        cellRenderer: (params: any) =>
          format(new Date(params.value), 'dd/MM/yyyy'),
      },
      {
        colId: FilterContractsFieldEnum.ExpiryDate,
        headerName: t('submission.expiryDate'),
        field: 'expiryDate',
        flex: 1,
        editable: false,
        minWidth: 150,
        resizable: true,
        unSortIcon: true,
        sortable: true,
        comparator: () => 0,
        cellRenderer: (params: any) =>
          format(new Date(params.value), 'dd/MM/yyyy'),
      },
      {
        colId: FilterContractsFieldEnum.ContractStatus,
        headerName: t('general.status'),
        field: 'status',
        flex: 1,
        editable: false,
        minWidth: 170,
        resizable: true,
        unSortIcon: true,
        sortable: true,
        comparator: () => 0,
        cellClass: 'ag-center-cell',
        cellRenderer: (params: {
          value: ContractResponseDtoContractStatusEnum;
        }) => <ContractStatusIndicator status={params.value} />,
      },
      {
        colId: FilterContractsFieldEnum.LayerType,
        headerName: t('contractDetail.layerType'),
        field: 'layerType',
        flex: 1,
        editable: false,
        minWidth: 150,
        resizable: true,
        unSortIcon: true,
        sortable: true,
        comparator: () => 0,
      },
      {
        colId: FilterContractsFieldEnum.LimitHundredPercent,
        headerName: t('layerStructure.headers.limit'),
        field: 'limitHundredPercent',
        flex: 1,
        editable: false,
        minWidth: 150,
        resizable: true,
        unSortIcon: true,
        sortable: true,
        comparator: () => 0,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 0,
      },
      {
        colId: FilterContractsFieldEnum.ExcessHundredPercent,
        headerName: t('layerStructure.headers.excess'),
        field: 'excessHundredPercent',
        flex: 1,
        editable: false,
        minWidth: 150,
        resizable: true,
        unSortIcon: true,
        sortable: true,
        comparator: () => 0,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 0,
      },
      {
        colId: FilterContractsFieldEnum.LineSizePercent,
        headerName: t('layerStructure.headers.lineSize'),
        field: 'lineSizePercent',
        flex: 1,
        editable: false,
        minWidth: 150,
        resizable: true,
        unSortIcon: true,
        sortable: true,
        comparator: () => 0,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 0,
      },
      {
        headerName: t('layerStructure.headers.layerPremium'),
        field: 'layerPremiumHundredPercent',
        flex: 1,
        editable: false,
        minWidth: 150,
        resizable: true,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 0,
      },
      {
        headerName: t('submission.shareOfPremiumSigned'),
        field: 'shareOfPremiumSigned',
        flex: 1,
        editable: false,
        minWidth: 150,
        resizable: true,
        type: ECellValueType.NUMERIC_COLUMN,
        precision: 0,
      },
      {
        headerName: t('contractDetail.pinnacleCommission'),
        field: 'pinnacleCommission',
        flex: 1,
        editable: false,
        minWidth: 150,
        resizable: true,
        type: ECellValueType.NUMERIC_COLUMN,
      },
      {
        field: 'contractId',
        hide: true,
      },
      {
        field: 'isSlipUploaded',
        hide: true,
      },
      {
        field: 'submissionId',
        hide: true,
      },
    ],
    [t]
  );

  const handleRowClick = useCallback(
    (row: RowClickedEvent<BinderContractData>) => {
      if (!row.data) return;
      const { status, isSlipUploaded, submissionId } = row.data;

      const isBoundContractHasNoSlip =
        status === ContractResponseDtoContractStatusEnum.Bound &&
        isSlipUploaded === false;

      const isNotBoundContract =
        status !== ContractResponseDtoContractStatusEnum.Bound;

      if (isBoundContractHasNoSlip || isNotBoundContract) {
        return navigate(constructSubmissionDetailPath(submissionId, '9'), {
          state: {
            to: EPageTo.CONTRACT_DETAIL,
            contractId: row.data.contractId,
            contractStatus: row.data.status,
          },
        });
      }

      return navigate(constructContractDetailPath(row.data.contractId));
    },
    [navigate]
  );

  const handleSortChanged = useCallback((event: SortChangedEvent) => {
    const sortedCol = event.api.getColumnState().find((col) => col.sort);
    if (sortedCol) {
      setSortField(sortedCol.colId as FilterContractsFieldEnum);
      setSortOrder(
        sortedCol.sort === 'asc'
          ? FilterContractsOrderEnum.Asc
          : FilterContractsOrderEnum.Desc
      );
    } else {
      setSortField(undefined);
      setSortOrder(undefined);
    }
  }, []);

  const binderContracts: BinderContractData[] = (contracts?.items ?? []).map(
    (contract, index) => {
      return {
        id: pageSize * (currentPage - 1) + index + 1,
        pinnacleRefNo: contract.pinnacleReferenceNumber,
        insured: contract.submission?.insured?.name,
        reinsured: contract.reinsured?.name,
        sourceBroker: contract.broker?.name,
        binder: contract.binder?.binderName,
        lineOfBusiness: contract.submission?.lineOfBusiness,
        assignedHandler: contract.submission?.assignedHandler?.name,
        namedUnderwriter: contract.submission?.namedUnderwriter?.name,
        underwritingYear: contract.binder?.underwritingYear,
        inceptionDate: getLocalDateForMidnightUTC(
          contract.submission?.inceptionDate ?? new Date()
        ),
        expiryDate: getLocalDateForMidnightUTC(
          contract.submission?.expiryDate ?? new Date()
        ),
        status: contract.contractStatus,
        layerType: contract.layer?.limitType,
        limitHundredPercent: contract.layer?.limit,
        excessHundredPercent: contract.layer?.excess,
        lineSizePercent: contract.layer?.lineSize,
        layerPremiumHundredPercent: contract.layer?.premium,
        shareOfPremiumSigned: contract.shareOfPremiumSigned,
        pinnacleCommission: contract.pinnacleCommission,
        contractId: contract.contractId,
        oldPinReferenceNumber: contract.oldPinReferenceNumber,
        isSlipUploaded: contract.isSlipUploaded,
        submissionId: contract.submissionId,
      };
    }
  );

  return (
    <div className="w-full">
      <Form form={form} className="w-full">
        <div className="flex gap-2 justify-between items-center mb-4">
          <BinderContractTabs
            activeTab={activeTab}
            setActiveTab={setActiveTab}
          />
          <FilterButton
            isActive={isFilterOpen}
            onClick={() => setIsFilterOpen(!isFilterOpen)}
          />
        </div>

        {isFilterOpen && (
          <div className="p-4 mb-4 bg-gray-50 rounded-lg border">
            <Space direction="horizontal" wrap className="w-full">
              <Form.Item name="pinnacleRefNo" className="min-w-[220px] mb-2">
                <Input
                  placeholder={t('contractAdjustment.pinnacleReferenceNumber')}
                  size="large"
                  className="rounded-xl"
                  allowClear
                />
              </Form.Item>

              <Form.Item name="legacyPinRefNo" className="min-w-[220px] mb-2">
                <Input
                  placeholder={t('contractAdjustment.legacyPINReferenceNumber')}
                  size="large"
                  className="rounded-xl"
                  allowClear
                />
              </Form.Item>

              <Form.Item name="insureds" className="min-w-[220px] mb-2">
                <SortedSelect
                  mode="multiple"
                  placeholder={t('insured.selectInsured')}
                  options={insuredOptions}
                  size="large"
                  maxTagCount={2}
                  className="rounded-xl"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    ((option?.label ?? '') as string)
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>

              <Form.Item name="reinsureds" className="min-w-[220px] mb-2">
                <SortedSelect
                  mode="multiple"
                  placeholder={t('general.selectField', {
                    field: t('reinsured.reinsured'),
                  })}
                  options={reinsuredOptions}
                  size="large"
                  maxTagCount={2}
                  className="rounded-xl"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    ((option?.label ?? '') as string)
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>

              <Form.Item name="brokers" className="min-w-[220px] mb-2">
                <SortedSelect
                  mode="multiple"
                  placeholder={t('general.selectField', {
                    field: t('broker.broker'),
                  })}
                  options={brokerOptions}
                  size="large"
                  maxTagCount={2}
                  className="rounded-xl"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    ((option?.label ?? '') as string)
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>

              <Form.Item name="binders" className="min-w-[220px] mb-2">
                <SortedSelect
                  mode="multiple"
                  placeholder={t('binder.selectBinder')}
                  options={binderOptions}
                  size="large"
                  maxTagCount={2}
                  className="rounded-xl"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    ((option?.label ?? '') as string)
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>

              <Form.Item name="lineOfBusiness" className="min-w-[220px] mb-2">
                <SortedSelect
                  mode="multiple"
                  placeholder={t('submission.selectLineOfBusiness')}
                  options={lineOfBusinessOptions}
                  size="large"
                  maxTagCount={2}
                  className="rounded-xl"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    ((option?.label ?? '') as string)
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>

              <Form.Item name="underwriters" className="min-w-[220px] mb-2">
                <SortedSelect
                  mode="multiple"
                  placeholder={t('general.selectField', {
                    field: t('submission.assignedHandler'),
                  })}
                  options={underwriterOptions}
                  size="large"
                  maxTagCount={2}
                  className="rounded-xl"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    ((option?.label ?? '') as string)
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>

              <Form.Item
                name="underwritingYears"
                className="min-w-[220px] mb-2"
              >
                <SortedSelect
                  mode="multiple"
                  placeholder={t('submission.selectUnderwritingYear')}
                  options={underwritingYearOptions}
                  size="large"
                  maxTagCount={2}
                  className="rounded-xl"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    ((option?.label ?? '') as string)
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>

              <Form.Item name="statuses" className="min-w-[220px] mb-2">
                <SortedSelect
                  mode="multiple"
                  placeholder={t('general.selectField', {
                    field: t('general.status'),
                  })}
                  options={statusOptions}
                  size="large"
                  maxTagCount={2}
                  className="rounded-xl"
                  allowClear
                  showSearch
                  filterOption={(input, option) =>
                    ((option?.value ?? '') as string)
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>
            </Space>
          </div>
        )}
      </Form>

      <ExcelTable
        data={binderContracts}
        columnDefs={columnDefs}
        rowKeyColName="id"
        onRowClicked={handleRowClick}
        tableContainerClassName="h-[30rem]"
        paginationType={EExcelTablePaginationType.DETAIL}
        currentPage={currentPage}
        pageSize={pageSize}
        totalRows={contracts?.pagination.total ?? 0}
        onPageChange={onPageChange}
        onPageSizeChange={onShowSizeChange}
        tableClassName="rounded-t-lg"
        className="mb-2"
        includeIdRow={true}
        onSortChanged={handleSortChanged}
        loading={isLoadingContracts}
        pageSizeOptions={[10, 20, 50, 100, 500, 1000]}
      />
    </div>
  );
};

export default BinderContractTable;
